/* eslint-disable no-console */
import pino from 'pino'
import { trace, context } from '@opentelemetry/api'
import { getConfig } from './config'
import { isBrowser } from './runtime'

const config = getConfig()

export const getSeverityForLevel = (level: number) => {
  // pino: trace=10, debug=20, info=30, warn=40, error=50, fatal=60
  // coralogix: 2xx=info, 4xx=error, 5xx=critical (other levels are debug)
  if (level >= 60) {
    return 500 + (level - 60)
  }
  if (level >= 50) {
    return 400 + (level - 50)
  }
  if (level >= 30) {
    return 200 + (level - 30)
  }
  return 100 + (level - 10)
}

/**
 * OpenTelemetry trace correlation mixin.
 * Works in both browser and Node.js environments.
 */
function createTraceMixin(): pino.LoggerOptions['mixin'] {
  return () => {
    const span = trace.getSpan(context.active())
    if (span) {
      const spanContext = span.spanContext()
      return {
        trace_id: spanContext.traceId,
        span_id: spanContext.spanId,
        trace_flags: spanContext.traceFlags,
      }
    }
    return {}
  }
}

/**
 * Base options shared between browser and Node.js loggers.
 */
export function createBaseOptions(): pino.LoggerOptions {
  return {
    level: config.logLevel,
    base: {
      service: 'next-app',
      environment: config.env,
    },
    mixin: createTraceMixin(),
    formatters: {
      level: (label, number) => ({
        level: number,
        level_label: label,
        severity: getSeverityForLevel(number),
      }),
    },
    serializers: {
      err: pino.stdSerializers.err,
      error: pino.stdSerializers.err,
    },
  }
}

/**
 * Creates a browser-compatible pino logger.
 * Uses console methods instead of process.stdout.
 */
function createBrowserLogger(): pino.Logger {
  const options: pino.LoggerOptions = {
    ...createBaseOptions(),
    browser: {
      asObject: true,
      write: {
        fatal: (o) => console.error(o),
        error: (o) => console.error(o),
        warn: (o) => console.warn(o),
        info: (o) => console.info(o),
        debug: (o) => console.debug(o),
        trace: (o) => console.trace(o),
      },
    },
  }
  return pino(options)
}

/**
 * Creates a Node.js pino logger with optional pretty printing.
 */
function createNodeLogger(): pino.Logger {
  const options = createBaseOptions()

  // Only attempt pretty printing in development
  if (config.env === 'development') {
    try {
      // Dynamic import to avoid bundler issues
      // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports
      const pretty = require('pino-pretty')
      const destination = pretty({
        colorize: true,
        translateTime: 'HH:MM:ss',
        ignore:
          'pid,hostname,service,environment,trace_id,span_id,trace_flags,level_label,severity',
        sync: true,
      })
      return pino(options, destination)
    } catch {
      // pino-pretty not available, fall back to default
    }
  }

  return pino(options)
}

/**
 * The logger instance, configured for the current runtime environment.
 */
export const logger: pino.Logger = isBrowser
  ? createBrowserLogger()
  : createNodeLogger()
